import {
  type ItemTypeValue,
  ObjectType,
  type ObjectTypeValue,
  TileType,
  type TileTypeValue,
} from '@dreamgame/shared';

// ============================================================================
// Render Constants - Shared visual configuration
// ============================================================================

// ============================================================================
// Tile Colors (fallback when textures not loaded)
// ============================================================================

export const TILE_COLORS: Record<TileTypeValue, number> = {
  // Base
  [TileType.VOID]: 0x000000,
  [TileType.GRASS]: 0x4a7c23,
  [TileType.DIRT]: 0x8b6914,
  [TileType.STONE]: 0x808080,
  [TileType.SAND]: 0xc2b280,
  [TileType.MUD]: 0x5a4a3a,

  // Water types
  [TileType.DEEP_WATER]: 0x1a4a7a,
  [TileType.WATER]: 0x2b6ca3,
  [TileType.SHALLOW_WATER]: 0x4a8fc3,
  [TileType.RIVER]: 0x3a7cb3,
  [TileType.FROZEN_WATER]: 0xa8d4e6,

  // Forest types
  [TileType.FOREST_FLOOR]: 0x2d5a1d,
  [TileType.FOREST_GRASS]: 0x3a6825,
  [TileType.PINE_NEEDLES]: 0x3d4a2a,

  // Desert types
  [TileType.SAND_DUNE]: 0xd4c090,
  [TileType.CRACKED_EARTH]: 0x9a8060,
  [TileType.ROCKY_SAND]: 0xb0a070,

  // Tundra types
  [TileType.SNOW]: 0xe8f0f8,
  [TileType.ICE]: 0xc0e0f0,
  [TileType.PERMAFROST]: 0x6a7a6a,
  [TileType.TUNDRA_GRASS]: 0x6a8a5a,

  // Swamp types
  [TileType.SWAMP_WATER]: 0x4a6a4a,
  [TileType.PEAT]: 0x3a3020,
  [TileType.MARSH_GRASS]: 0x4a6a3a,
  [TileType.REEDS]: 0x5a7a4a,

  // Mountain types
  [TileType.GRAVEL]: 0x6a6a6a,
  [TileType.CLIFF]: 0x4a4a4a,

  // Beach types
  [TileType.WET_SAND]: 0xa09a70,
  [TileType.PEBBLES]: 0x8a8a7a,
};

// ============================================================================
// Object Colors (fallback when textures not loaded)
// ============================================================================

export const OBJECT_COLORS: Partial<Record<ObjectTypeValue, { fill: number; stroke: number }>> = {
  // Trees
  [ObjectType.TREE_OAK]: { fill: 0x2d6a1e, stroke: 0x5c4033 },
  [ObjectType.TREE_PINE]: { fill: 0x1a5c32, stroke: 0x4a3728 },
  [ObjectType.TREE_BIRCH]: { fill: 0x4a9e3c, stroke: 0xe8e4d4 },
  [ObjectType.BUSH]: { fill: 0x3d8c2e, stroke: 0x2a5c1f },
  // Rocks
  [ObjectType.ROCK_SMALL]: { fill: 0x787878, stroke: 0x5a5a5a },
  [ObjectType.ROCK_MEDIUM]: { fill: 0x686868, stroke: 0x4a4a4a },
  [ObjectType.ROCK_LARGE]: { fill: 0x585858, stroke: 0x3a3a3a },
  [ObjectType.BOULDER]: { fill: 0x505050, stroke: 0x303030 },
  // Sticks & Branches
  [ObjectType.STICKS]: { fill: 0x8b7355, stroke: 0x6b5335 },
  [ObjectType.BRANCH]: { fill: 0x7a6245, stroke: 0x5a4225 },
  // Forest undergrowth
  [ObjectType.MUSHROOM_CLUSTER]: { fill: 0xc4a882, stroke: 0x8b6914 },
  [ObjectType.FALLEN_LOG]: { fill: 0x6b5335, stroke: 0x4a3a25 },
  [ObjectType.WILD_FLOWERS]: { fill: 0xdd88aa, stroke: 0xaa5577 },
  [ObjectType.TREE_STUMP]: { fill: 0x7a6245, stroke: 0x5c4033 },
  [ObjectType.FERN]: { fill: 0x3a8a3a, stroke: 0x2a6a2a },
  // Walls
  [ObjectType.WALL_WOOD]: { fill: 0x8b6914, stroke: 0x5c4033 },
  [ObjectType.WALL_STONE]: { fill: 0x707070, stroke: 0x4a4a4a },
  [ObjectType.WALL_METAL]: { fill: 0x8899aa, stroke: 0x556677 },
  // Doors
  [ObjectType.DOOR_WOOD]: { fill: 0xa07020, stroke: 0x6c4c18 },
  [ObjectType.DOOR_METAL]: { fill: 0x7788aa, stroke: 0x445566 },
  // Ores
  [ObjectType.ORE_IRON]: { fill: 0x8b7355, stroke: 0xcd853f },
  [ObjectType.ORE_GOLD]: { fill: 0xffd700, stroke: 0xb8860b },
  [ObjectType.ORE_COPPER]: { fill: 0xb87333, stroke: 0x8b5a2b },
  [ObjectType.ORE_TIN]: { fill: 0x808080, stroke: 0x606060 },
  [ObjectType.ORE_COAL]: { fill: 0x2a2a2a, stroke: 0x1a1a1a },
  [ObjectType.ORE_SILVER]: { fill: 0xc0c0c0, stroke: 0x909090 },
  [ObjectType.ORE_MITHRIL]: { fill: 0x40e0d0, stroke: 0x20b0a0 },
  [ObjectType.ORE_STARMETAL]: { fill: 0x9932cc, stroke: 0x6a0dad },
  // Large ore deposits (same colors as small)
  [ObjectType.ORE_TIN_LARGE]: { fill: 0x808080, stroke: 0x606060 },
  [ObjectType.ORE_COPPER_LARGE]: { fill: 0xb87333, stroke: 0x8b5a2b },
  [ObjectType.ORE_IRON_LARGE]: { fill: 0x8b7355, stroke: 0xcd853f },
  [ObjectType.ORE_COAL_LARGE]: { fill: 0x2a2a2a, stroke: 0x1a1a1a },
  [ObjectType.ORE_SILVER_LARGE]: { fill: 0xc0c0c0, stroke: 0x909090 },
  [ObjectType.ORE_GOLD_LARGE]: { fill: 0xffd700, stroke: 0xb8860b },
  [ObjectType.ORE_MITHRIL_LARGE]: { fill: 0x40e0d0, stroke: 0x20b0a0 },
  [ObjectType.ORE_STARMETAL_LARGE]: { fill: 0x9932cc, stroke: 0x6a0dad },
  // Furniture
  [ObjectType.BED]: { fill: 0xe8dcc8, stroke: 0x8b6914 },
  // Territory Flags
  [ObjectType.CLAIM_FLAG_HOME]: { fill: 0x3366aa, stroke: 0xccaa44 },
  [ObjectType.CLAIM_FLAG_OUTPOST]: { fill: 0xaa4444, stroke: 0x8b6914 },
  // Workstations
  [ObjectType.CRAFTING_TABLE]: { fill: 0x8b6914, stroke: 0x5c4033 },
  [ObjectType.ANVIL]: { fill: 0x4a4a4a, stroke: 0x2a2a2a },
  [ObjectType.CAMPFIRE]: { fill: 0xd4640a, stroke: 0x8b4513 },
  [ObjectType.COOKING_STOVE]: { fill: 0x808080, stroke: 0x4a4a4a },
  [ObjectType.FURNACE]: { fill: 0x8b4513, stroke: 0x5c3317 },
  // Ruins/Graveyard
  [ObjectType.GRAVESTONE]: { fill: 0x606060, stroke: 0x404040 },
  [ObjectType.RUINED_CRYPT]: { fill: 0x505050, stroke: 0x303030 },
  [ObjectType.GRAVE_MOUND]: { fill: 0x4a3a2a, stroke: 0x3a2a1a },
  // Fishing Spots
  [ObjectType.FISHING_SPOT_SHORE]: { fill: 0x4a8fc3, stroke: 0x2b6ca3 },
  [ObjectType.FISHING_SPOT_RIVER]: { fill: 0x3a7cb3, stroke: 0x1a5c93 },
  [ObjectType.FISHING_SPOT_OCEAN]: { fill: 0x2b6ca3, stroke: 0x1a4a7a },
  [ObjectType.FISHING_SPOT_SWAMP]: { fill: 0x4a6a4a, stroke: 0x3a5a3a },
};

// ============================================================================
// Object Texture Names
// ============================================================================

export const OBJECT_TEXTURE_NAMES: Partial<Record<ObjectTypeValue, string>> = {
  [ObjectType.TREE_OAK]: 'tree_oak',
  [ObjectType.TREE_PINE]: 'tree_pine',
  [ObjectType.TREE_BIRCH]: 'tree_birch',
  [ObjectType.BUSH]: 'bush',
  [ObjectType.ROCK_SMALL]: 'rock_small',
  [ObjectType.ROCK_MEDIUM]: 'rock_medium',
  [ObjectType.ROCK_LARGE]: 'rock_large',
  [ObjectType.BOULDER]: 'boulder',
  [ObjectType.STICKS]: 'sticks',
  [ObjectType.BRANCH]: 'branch',
  [ObjectType.WALL_WOOD]: 'wall_wood',
  [ObjectType.WALL_STONE]: 'wall_stone',
  [ObjectType.WALL_METAL]: 'wall_metal',
  [ObjectType.DOOR_WOOD]: 'door_wood',
  [ObjectType.DOOR_METAL]: 'door_metal',
  [ObjectType.ORE_IRON]: 'ore_iron',
  [ObjectType.ORE_GOLD]: 'ore_gold',
  [ObjectType.ORE_COPPER]: 'ore_copper',
  [ObjectType.ORE_TIN]: 'ore_tin',
  [ObjectType.ORE_COAL]: 'ore_coal',
  [ObjectType.ORE_SILVER]: 'ore_silver',
  [ObjectType.ORE_MITHRIL]: 'ore_mithril',
  [ObjectType.ORE_STARMETAL]: 'ore_starmetal',
  // Large ore deposits (use same textures as small)
  [ObjectType.ORE_TIN_LARGE]: 'ore_tin',
  [ObjectType.ORE_COPPER_LARGE]: 'ore_copper',
  [ObjectType.ORE_IRON_LARGE]: 'ore_iron',
  [ObjectType.ORE_COAL_LARGE]: 'ore_coal',
  [ObjectType.ORE_SILVER_LARGE]: 'ore_silver',
  [ObjectType.ORE_GOLD_LARGE]: 'ore_gold',
  [ObjectType.ORE_MITHRIL_LARGE]: 'ore_mithril',
  [ObjectType.ORE_STARMETAL_LARGE]: 'ore_starmetal',
  [ObjectType.CAMPFIRE]: 'campfire',
  [ObjectType.STORAGE_CRATE]: 'storage_crate',
  [ObjectType.TABLE]: 'table',
  [ObjectType.CHAIR]: 'chair',
  [ObjectType.BED]: 'bed',
  [ObjectType.SLEEPING_PAD]: 'sleeping_pad',
  [ObjectType.CLAIM_FLAG_HOME]: 'claim_flag_home',
  [ObjectType.CLAIM_FLAG_OUTPOST]: 'claim_flag_outpost',
  // Workstations
  [ObjectType.CRAFTING_TABLE]: 'crafting_table',
  [ObjectType.ANVIL]: 'anvil',
  [ObjectType.COOKING_STOVE]: 'cooking_stove',
  [ObjectType.FURNACE]: 'furnace',
  // Ruins/Graveyard
  [ObjectType.GRAVESTONE]: 'gravestone',
  [ObjectType.RUINED_CRYPT]: 'ruined_crypt',
  [ObjectType.GRAVE_MOUND]: 'grave_mound',
  // Fishing Spots
  [ObjectType.FISHING_SPOT_SHORE]: 'fishing_spot_shore',
  [ObjectType.FISHING_SPOT_RIVER]: 'fishing_spot_river',
  [ObjectType.FISHING_SPOT_OCEAN]: 'fishing_spot_ocean',
  [ObjectType.FISHING_SPOT_SWAMP]: 'fishing_spot_swamp',
  // Forest undergrowth
  [ObjectType.MUSHROOM_CLUSTER]: 'mushroom_cluster',
  [ObjectType.FALLEN_LOG]: 'fallen_log',
  [ObjectType.WILD_FLOWERS]: 'wild_flowers',
  [ObjectType.TREE_STUMP]: 'tree_stump',
  [ObjectType.FERN]: 'fern',
};

// ============================================================================
// Tile Names for Texture Loading
// ============================================================================

export const TILE_TEXTURE_NAMES: Array<{ type: TileTypeValue; name: string }> = [
  // Base
  { type: TileType.GRASS, name: 'grass' },
  { type: TileType.DIRT, name: 'dirt' },
  { type: TileType.STONE, name: 'stone' },
  { type: TileType.SAND, name: 'sand' },
  { type: TileType.MUD, name: 'mud' },

  // Water
  { type: TileType.DEEP_WATER, name: 'deep_water' },
  { type: TileType.WATER, name: 'water' },
  { type: TileType.SHALLOW_WATER, name: 'shallow_water' },
  { type: TileType.RIVER, name: 'river' },
  { type: TileType.FROZEN_WATER, name: 'frozen_water' },

  // Forest
  { type: TileType.FOREST_FLOOR, name: 'forest_floor' },
  { type: TileType.FOREST_GRASS, name: 'forest_grass' },
  { type: TileType.PINE_NEEDLES, name: 'pine_needles' },

  // Desert
  { type: TileType.SAND_DUNE, name: 'sand_dune' },
  { type: TileType.CRACKED_EARTH, name: 'cracked_earth' },
  { type: TileType.ROCKY_SAND, name: 'rocky_sand' },

  // Tundra
  { type: TileType.SNOW, name: 'snow' },
  { type: TileType.ICE, name: 'ice' },
  { type: TileType.PERMAFROST, name: 'permafrost' },
  { type: TileType.TUNDRA_GRASS, name: 'tundra_grass' },

  // Swamp
  { type: TileType.SWAMP_WATER, name: 'swamp_water' },
  { type: TileType.PEAT, name: 'peat' },
  { type: TileType.MARSH_GRASS, name: 'marsh_grass' },
  { type: TileType.REEDS, name: 'reeds' },

  // Mountain
  { type: TileType.GRAVEL, name: 'gravel' },
  { type: TileType.CLIFF, name: 'cliff' },

  // Beach
  { type: TileType.WET_SAND, name: 'wet_sand' },
  { type: TileType.PEBBLES, name: 'pebbles' },
];

// ============================================================================
// Ground Item Colors
// ============================================================================

// Specific item type colors
const RAW_MATERIAL_COLORS: Record<number, number> = {
  1: 0x8b6914, // Wood - brown
  2: 0x808080, // Stone - gray
  3: 0x8b7355, // Iron ore
  4: 0xffd700, // Gold ore
  5: 0xb87333, // Copper ore
};

const getColorByRange = ({ itemType }: { itemType: number }): number | null => {
  // Raw materials: browns/grays (1-99)
  if (itemType >= 1 && itemType <= 99) {
    return RAW_MATERIAL_COLORS[itemType] ?? 0x666666;
  }
  // Processed materials: shiny (100-199)
  if (itemType >= 100 && itemType <= 199) {
    return 0xaaaaaa;
  }
  // Food: greens/reds (200-299)
  if (itemType >= 200 && itemType <= 299) {
    return 0x66aa44;
  }
  return null;
};

export const getGroundItemColor = ({ itemType }: { itemType: ItemTypeValue }): number => {
  return getColorByRange({ itemType }) ?? 0x888888;
};

// ============================================================================
// Visual Constants
// ============================================================================

// Pawn rendering (smaller than tile for visual breathing room like RimWorld)
export const PAWN_RENDER_SIZE = 24;
export const PAWN_OFFSET = (32 - PAWN_RENDER_SIZE) / 2; // Center in tile
export const PAWN_PADDING = PAWN_OFFSET; // Legacy alias
export const PAWN_SIZE = PAWN_RENDER_SIZE; // Legacy alias

// Health bar dimensions
export const HEALTH_BAR_HEIGHT = 4;
export const HEALTH_BAR_OFFSET_Y = -8;

// Colors
export const COLORS = {
  // Selection
  SELECTION_FILL: 0x44ff44,
  SELECTION_STROKE: 0x44ff44,
  SELECTION_ALPHA: 0.15,

  // Task target highlight (blue halo around objects being worked on)
  TASK_TARGET_STROKE: 0x4488ff,

  // Task progress indicator (circular progress for harvesting)
  TASK_PROGRESS_BG: 0x222222,
  TASK_PROGRESS_FILL: 0x44cc44,

  // Fishing activity indicator (animated pulse/ripple)
  FISHING_INDICATOR_CORE: 0x5dc8ff,
  FISHING_INDICATOR_RING: 0x9ce6ff,
  SLEEP_INDICATOR_Z: 0xe3eeff,

  // Health bar
  HEALTH_HIGH: 0x44cc44,
  HEALTH_MEDIUM: 0xcccc44,
  HEALTH_LOW: 0xcc4444,
  HEALTH_BACKGROUND: 0x222222,

  // Pawn ownership
  PAWN_OWNED: 0x4488cc,
  PAWN_ENEMY: 0xcc8844,

  // Path visualization
  PATH_LINE: 0xffffff,
  PATH_DESTINATION: 0xffffff,
  PATH_WAYPOINT: 0xffffff,

  // Queue visualization
  QUEUE_LINE: 0x88bbff,
  QUEUE_MARKER: 0x88bbff,

  // Debug
  CHUNK_BORDER: 0x4444aa,
  TILE_GRID: 0x333333,
  ORIGIN_X: 0xff4444,
  ORIGIN_Y: 0x44ff44,

  // Unloaded chunk
  UNLOADED_CHUNK: 0x1a1a2e,
} as const;

// ============================================================================
// Water Effects - Pseudo-3D depth rendering
// ============================================================================

export const WATER_EFFECTS = {
  // Shore shadow (drawn on water tiles adjacent to land)
  SHADOW_HEIGHT: 10, // Shadow extends 10px into water
  SHADOW_COLOR: 0x000000,
  SHADOW_ALPHA_MAX: 0.4, // Max opacity at shore edge

  // Land highlight (drawn on land tiles adjacent to water)
  HIGHLIGHT_WIDTH: 2, // Highlight thickness in pixels
  HIGHLIGHT_COLOR: 0xffffff,
  HIGHLIGHT_ALPHA: 0.25, // Subtle highlight

  // Corner shadow for diagonal adjacency
  CORNER_SHADOW_SIZE: 8,
  CORNER_SHADOW_ALPHA: 0.3,
} as const;
